import { GeneralCloseIcon } from '@outdoorsyco/bonfire';
import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';

import Loading from '@/components/switchback/Loading/Loading';
import { uploadImage } from '@/services/uploadImage';

interface IImageUploader {
  label: string;
  rentalId: number;
  images: string[];
  onUploadImage: (image: string[]) => void;
  onDeleteImage: (image: string) => void;
  /** When true the dropzone stretches the full width of its container (MP-5691 modal step 4). */
  fullWidth?: boolean;
}

const ImageUploader: React.FC<IImageUploader> = ({
  label,
  rentalId,
  images,
  onUploadImage,
  onDeleteImage,
  fullWidth = false,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const onDrop = useCallback(
    async <T extends File>(acceptedFiles: T[]) => {
      try {
        setIsLoading(true);
        const uploadPromise = acceptedFiles.map(file =>
          uploadImage(file, `/rentals/${rentalId}/review/images`),
        );
        const imagesUploaded = await Promise.all(uploadPromise);
        setIsLoading(false);
        onUploadImage(
          imagesUploaded.map(uploaded => {
            return uploaded?.secure_url
              .split('.')
              .map(segment => (segment !== 'heic' && segment !== 'heif' ? segment : 'jpg'))
              .join('.');
          }),
        );
      } catch {
        setIsLoading(false);
      }
    },
    [rentalId, onUploadImage],
  );

  const onClickImage = (image: string) => {
    onDeleteImage(image);
  };

  const { getInputProps, getRootProps } = useDropzone({
    accept: ['image/png', 'image/jpg', 'image/jpeg', 'image/heic', 'image/heif'],
    onDrop,
  });
  return (
    <div className="flex flex-col w-full">
      <div
        {...getRootProps()}
        className={`relative flex flex-col items-center justify-center border rounded h-44 text-gray-250 border-gray-250 ${
          fullWidth ? 'w-full' : 'w-1/2'
        }`}>
        <input {...getInputProps()} className="hidden" data-testid="image-upload" />
        {!isLoading ? (
          <div className="flex items-center justify-center pb-1 text-lg text-center border rounded-full w-9 h-9 border-gray-250">
            +
          </div>
        ) : (
          <Loading />
        )}
        <p className="mt-2 text-gray-800">{label}</p>
      </div>
      {Boolean(images?.length) && (
        <ul className="w-full mt-4 grid grid-cols-2 gap-4 md:grid-cols-3">
          {images.map(image => (
            <li className="relative w-full rounded" key={image}>
              <button
                className="absolute p-1 bg-white  rounded-full shadow-md right-1 top-1"
                data-testid={image}
                onClick={() => onClickImage(image)}>
                <GeneralCloseIcon fontSize={16} className="font-light" />
              </button>
              <img src={image} alt={image} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ImageUploader;
